import type { ChangeEvent, ReactElement } from 'react';
import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useDateFormat, useTranslation } from '@eti/providers';
import { format as formatDateFns, isValid, parse } from 'date-fns';
import { useFormContext, useWatch } from 'react-hook-form';
import merge from 'lodash/merge';
import FormInput from '../../../common/components/formComponents/formInput/FormInput';
import {
  isRequired,
  isValidDate,
  isAfterDate,
  isBeforeDate,
  isNotNull,
} from '../../../common/components/formComponents/validations';
import { getFormattedValueFromString } from '../utils/dates';
import useFocus from '../utils/useFocus';

const isValidDateTyped = (date: unknown): date is Date => isValid(date);

export interface DateInputNewProps {
  'aria-label'?: string;
  placeholder: string;
  name: string;
  label: string;
  afterDate?: Date;
  beforeDate?: Date;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  iconSuffix?: ReactElement;
}

const DateInputNew = forwardRef<HTMLInputElement, DateInputNewProps>(
  (
    {
      iconSuffix,
      'aria-label': ariaLabel,
      placeholder,
      name,
      label,
      afterDate,
      beforeDate,
      onChange,
      onBlur,
      onFocus,
    },
    ref,
  ) => {
    const { getValues, trigger: validateField, control, setValue, formState } = useFormContext();
    const fieldValue = useWatch({
      control,
      name,
    });
    const { date: dateFormat } = useDateFormat();
    const [tempInputValue, setTempInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const userIsTyping = useFocus(inputRef);
    const { t } = useTranslation();

    useImperativeHandle(ref, () => inputRef.current!, []);

    useEffect(() => {
      if (!fieldValue && inputRef.current) {
        setValue(name, null);
        setTempInputValue('');
      }
    }, [fieldValue, name, setValue]);

    const handleFocus = () => {
      onFocus?.();

      const currentValue = getValues(name);
      if (isValid(currentValue)) {
        setTempInputValue(formatDateFns(currentValue, dateFormat));
      }
    };

    const normalize = (value: Date | string): Date => {
      if (typeof value === 'object') {
        return value;
      }

      const formattedString = getFormattedValueFromString(value, dateFormat);
      return parse(formattedString, dateFormat, new Date());
    };

    const format = (value: Date): string =>
      isValid(value) ? formatDateFns(value, dateFormat) : tempInputValue;

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);

      if (formState.isSubmitted) {
        validateField();
      }

      const input = event.target;
      const { value } = input;

      if (value === '') {
        setValue(name, null);
      }

      const formatted = getFormattedValueFromString(value, dateFormat);
      setTempInputValue(formatted);

      const numberOfAddedSeparators = formatted.length - value.length;
      const cursorPosition = input.selectionStart
        ? input.selectionStart + numberOfAddedSeparators
        : null;

      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = cursorPosition;
          inputRef.current.selectionEnd = cursorPosition;
        }
      });
    };

    const handleBlur = () => {
      validateField();
      onBlur?.();
    };

    const formattedMinDate = isValidDateTyped(afterDate)
      ? formatDateFns(afterDate, dateFormat)
      : '';
    const formattedMaxDate = isValidDateTyped(beforeDate)
      ? formatDateFns(beforeDate, dateFormat)
      : '';

    const validations = merge(
      isRequired(t('Validation.Required')),
      isNotNull(t('Validation.Required')),
      isValidDate(t('Content.SearchForm.Errors.InvalidDateFormat', dateFormat)),
      isAfterDate(afterDate, t('Content.SearchForm.Errors.EnterDateAfter', formattedMinDate)),
      isBeforeDate(beforeDate, t('Content.SearchForm.Errors.EnterDateBefore', formattedMaxDate)),
    );

    const valueOverride = userIsTyping ? tempInputValue : undefined;

    return (
      <FormInput
        aria-label={ariaLabel}
        format={format}
        iconSuffix={iconSuffix}
        label={label}
        name={name}
        normalize={normalize}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        ref={inputRef}
        validations={validations}
        valueOverride={valueOverride}
      />
    );
  },
);

export default DateInputNew;
